import { useGetQuery, useGetQueryCollection, usePostMutation, usePutMutation } from 'shared/lib/api';
import { pathToUrl } from 'shared/lib/router';
import { useDownloadFile } from 'shared/lib/hooks';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import type {
  ArchivedInvoiceModel,
  ContractorArchivedInvoiceModel,
  ContractorInvoiceModel,
  InvoiceModel,
  SalesCampaignInvoice,
  SalesInvoiceUpdateModel,
  InvoicesStatus,
} from '../model';

import { invoicesRoutes } from './routes';

export const contractorInvoicesKey = createQueryKeys('contractorInvoices', {
  invoicesList: [''],
});

export const useGetContractorInvoices = ({ urlFilters, enabled }: { urlFilters?: string; enabled?: boolean }) =>
  useGetQueryCollection<ContractorInvoiceModel>(invoicesRoutes.app.getInvoices, {
    queryKey: [contractorInvoicesKey.invoicesList.queryKey, urlFilters],
    enabled,
    urlFilters,
    refetchOnMount: false,
    keepPreviousData: true,
  });

export const contractorInvoicesArchiveKey = createQueryKeys('contractorArchiveInvoices', {
  invoicesList: [''],
});

export const useGetContractorArchivedInvoices = ({ urlFilters, enabled }: { urlFilters?: string; enabled?: boolean }) =>
  useGetQueryCollection<ContractorArchivedInvoiceModel>(invoicesRoutes.app.getArchivedInvoices, {
    queryKey: [contractorInvoicesArchiveKey.invoicesList.queryKey, urlFilters],
    enabled,
    urlFilters,
    refetchOnMount: false,
    keepPreviousData: true,
  });

export const managerInvoicesKey = createQueryKeys('invoicesManager', {
  invoicesList: [''],
});

export const useGetManagerInvoices = ({ urlFilters, enabled }: { urlFilters?: string; enabled?: boolean }) =>
  useGetQueryCollection<InvoiceModel>(invoicesRoutes.internal.getInvoices, {
    queryKey: [managerInvoicesKey.invoicesList.queryKey, urlFilters],
    enabled,
    urlFilters,
    refetchOnMount: false,
    keepPreviousData: true,
  });

export const managerArchivedInvoicesKey = createQueryKeys('archivedInvoicesManager', {
  invoicesList: [''],
});

export const useGetManagerArchivedInvoices = ({ urlFilters, enabled }: { urlFilters?: string; enabled?: boolean }) =>
  useGetQueryCollection<ArchivedInvoiceModel>(invoicesRoutes.internal.getArchivedInvoices, {
    queryKey: [managerArchivedInvoicesKey.invoicesList.queryKey, urlFilters],
    enabled,
    urlFilters,
    refetchOnMount: false,
    keepPreviousData: true,
  });

export const useChargeInvoice = (id: number) =>
  usePostMutation(pathToUrl(invoicesRoutes.internal.chargeInvoice, { id }));

export const useUpdateInvoiceStatus = (id: number) =>
  usePutMutation<{ status: InvoicesStatus }>(pathToUrl(invoicesRoutes.internal.updateInvoiceStatus, { id }));

export const useGetManagerInvoicesCSV = (urlFilters: string | undefined) =>
  useDownloadFile({
    url: invoicesRoutes.internal.getInvoicesCSV,
    urlFilters,
  });

export const useGetInvoicesSalesCampaigns = (id: number) =>
  useGetQuery<SalesCampaignInvoice[]>(invoicesRoutes.internal.getInvoicesSalesCampaigns, {
    urlParams: { id },
  });

export const useUpdateSalesInvoice = (id: number) =>
  usePostMutation<SalesInvoiceUpdateModel>(pathToUrl(invoicesRoutes.internal.getInvoicesSalesCampaigns, { id }));

export const useGetManagerCurrentInvoicePdf = (id: number) =>
  useDownloadFile({ url: pathToUrl(invoicesRoutes.internal.downloadCurrentInvoice, { id }), type: 'pdf' });

export const useGetContractorCurrentInvoicePdf = (id: number) =>
  useDownloadFile({ url: pathToUrl(invoicesRoutes.app.downloadCurrentInvoice, { id }), type: 'pdf' });

export const useGetManagerArchivedInvoicePdf = (id: number) =>
  useDownloadFile({ url: pathToUrl(invoicesRoutes.internal.downloadArchivedInvoice, { id }), type: 'pdf' });

export const useGetContractorArchivedInvoicePdf = (id: number) =>
  useDownloadFile({ url: pathToUrl(invoicesRoutes.app.downloadArchivedInvoice, { id }), type: 'pdf' });
